import React, { useState, useEffect } from "react";
import { api } from "../api";

export default function EmailEditor({ lead, onUpdate }) {
  const [subject, setSubject] = useState(lead.email_subject || "");
  const [body, setBody] = useState(lead.email_body || "");
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setSubject(lead.email_subject || "");
    setBody(lead.email_body || "");
    setFeedback("");
    setMessage("");
  }, [lead.id, lead.email_subject, lead.email_body]);

  const isPending = lead.status === "pending";

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      const updated = await api(`/leads/${lead.id}/email`, {
        method: "PATCH",
        body: JSON.stringify({ subject, body }),
      });
      onUpdate(updated);
      setMessage("Saved");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    setMessage("");
    try {
      const updated = await api(`/leads/${lead.id}/regenerate`, {
        method: "POST",
        body: JSON.stringify({ feedback }),
      });
      onUpdate(updated);
      setFeedback("");
      setMessage("Email regenerated");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm("Send this email now?")) return;
    setApproving(true);
    setMessage("");
    try {
      const updated = await api(`/leads/${lead.id}/approve`, {
        method: "PATCH",
      });
      onUpdate(updated);
      setMessage("Email sent!");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm("Reject this lead?")) return;
    setRejecting(true);
    setMessage("");
    try {
      const updated = await api(`/leads/${lead.id}/reject`, {
        method: "PATCH",
      });
      onUpdate(updated);
      setMessage("Lead rejected");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div className="email-editor">
      <h4>Email Draft</h4>

      <label>Subject</label>
      <input
        type="text"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        disabled={!isPending}
      />

      <label>Body</label>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={8}
        disabled={!isPending}
      />

      {isPending && (
        <>
          <div className="editor-actions">
            <button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Draft"}
            </button>
            <button className="btn-approve" onClick={handleApprove} disabled={approving}>
              {approving ? "Sending..." : "Approve & Send"}
            </button>
            <button className="btn-reject" onClick={handleReject} disabled={rejecting}>
              {rejecting ? "Rejecting..." : "Reject"}
            </button>
          </div>

          <div className="regenerate-section">
            <label>Feedback for regeneration</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              placeholder="e.g. Make it more casual, mention their ABM goals..."
            />
            <button onClick={handleRegenerate} disabled={regenerating || !feedback}>
              {regenerating ? "Regenerating..." : "Regenerate Email"}
            </button>
          </div>
        </>
      )}

      {message && <p className="editor-message">{message}</p>}
    </div>
  );
}
